import React, {
	useEffect,
	useRef,
	useState,
	useCallback,
} from 'react';
import { IconBaseProps } from 'react-icons';
import { useField } from '@unform/core';
import { FiAlertCircle } from 'react-icons/fi';
import Select, { Props as SelectedProps } from 'react-select';

import { Container, Error } from './styles';
import { Flex } from '@chakra-ui/react';

type OptionData = {
	value: string;
	label: string;
};
interface SelectProps extends SelectedProps {
	name: string;
	icon?: React.ComponentType<IconBaseProps>;
	containerStyle?: object;
	options: OptionData[];
	placeholder?: string;
	onChange?: (e: any) => void;
};

const SelectInput: React.FC<SelectProps> = ({
	name,
	icon: Icon,
	options,
	placeholder,
	containerStyle,
	onChange,
	...rest
}) => {
	const inputRef = useRef<any>(null);
	const iconRef = useRef<any>(null);

	const [isFocused, setIsFocused] = useState(false);
	const [isField, setIsField] = useState(false);
	const { fieldName, defaultValue, error, registerField } = useField(name);

	const [selected, setSelected] = useState<OptionData | undefined>(() => {
		if (!defaultValue) return undefined;
		options.find((o) => o.value === defaultValue)
	});

	const handleInputFocus = useCallback(() => {
		setIsFocused(true);
	}, []);

	const handleInputBlur = useCallback(() => {
		setIsFocused(false);
		setIsField(!!inputRef.current?.value);
	}, []);

	const handleChange = (selectedOption: any) => {
		setSelected(selectedOption);

		if (onChange)
			onChange(selectedOption);
	};

	const selectedRef = useRef(selected);

	useEffect(() => {
		selectedRef.current = selected;
	}, [selected]);

	useEffect(() => {
		registerField({
			name: fieldName,
			ref: inputRef.current,
			path: undefined,
			getValue: () => {
				const val = selectedRef.current;
				return val?.value ?? '';
			},
			setValue: (ref, value) => {
				const option = options.find(option => option.value === value);

				setSelected(option);            // visual
				selectedRef.current = option;   // real valor para getValue

				ref.setValue(value);
			},
			clearValue: (ref) => {
				setSelected(undefined);
				selectedRef.current = undefined;
				ref.clearValue();
			},
		});
	}, [fieldName, registerField]);

	return (
		<Container
			isErrored={!!error}
			isField={isField}
			isFocused={isFocused}
			style={containerStyle}
		>
			{Icon && <Icon size={20} />}

			<Select
				options={options}
				defaultValue={defaultValue}
				placeholder={placeholder}
				noOptionsMessage={() => 'sem opções'}
				isSearchable
				isClearable={false}
				onFocus={handleInputFocus}
				onBlur={handleInputBlur}
				// onChange={handleChange}
				value={selected}
				onChange={(val: any) => handleChange(val)}

				ref={inputRef}
				{...rest}

				styles={{
					container: (defaultStyles) => ({
						...defaultStyles,
						flex: 1,
						padding: '0',
					}),
					control: (baseStyles) => ({
						...baseStyles,
						borderRadius: 6,
						border: 'none',
						color: "#333",
						fontWeight: 500,
						boxShadow: 'none',
						cursor: 'pointer'
					}),
					placeholder: (defaultStyles) => ({
						...defaultStyles,
						color: "#90a4ae"
					}),
					indicatorSeparator: (defaultStyles) => ({
						...defaultStyles,
						display: 'none',
					}),
					indicatorsContainer: (defaultStyles) => ({
						...defaultStyles,
						padding: '0',
						svg: {
							marginRight: '8px',
						}
					}),
					dropdownIndicator: defaultStyles => ({
						...defaultStyles,
						color: "#333",
						padding: '0',
						'&:hover': {
							color: '#2196f3'
						},
					}),
					menu: (defaultStyles) => ({
						...defaultStyles,
						position: "absolute",
					}),
					menuList: (defaultValue) => ({
						...defaultValue,
						maxHeight: 130,
						padding: 0
					}),
					option: (defaultValue) => ({
						...defaultValue,
						minHeight: 32,       // 👈 altura de cada opção
						paddingTop: 4,
						paddingBottom: 4,
						fontSize: 14,
					}),
					loadingIndicator: (defaultStyles) => ({
						...defaultStyles,
						color: '#2196f3'
					})
				}}
			/>

			{error && (
				<Error title={error}>
					<Flex pr={1} ref={iconRef}>
						<FiAlertCircle color="#c53030" size={20} />
					</Flex>
				</Error>
			)}
		</Container>
	);
};

export default SelectInput;